import { m } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { ResultsTree } from '../../eval/eval-interface/results-tree'
import { useEvalStore } from '../../eval/store'
import { DURATION, EASE_OUT } from '../motion'
import { type EvalChatMessage, EvalMessageStatus } from './eval-store'

interface EvalMessageRendererProps {
  message: EvalChatMessage
}

// Provide human-friendly feedback when simulations have not yet produced nodes.
const renderEvalEmptyState = (evalStatus?: EvalChatMessage['evalStatus']) => {
  if (evalStatus === EvalMessageStatus.Started || evalStatus === EvalMessageStatus.Running) {
    return (
      <div className="flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Generating test cases and running simulations...</span>
      </div>
    )
  }

  if (evalStatus === EvalMessageStatus.Error) {
    return <span className="text-destructive">Simulations failed. Please try again.</span>
  }

  if (evalStatus === EvalMessageStatus.Cancelled) {
    return <span className="text-secondary">Simulation cancelled by Vibe Agent.</span>
  }

  return <span>Waiting for simulations to start...</span>
}

export const EvalMessageRenderer = ({ message }: EvalMessageRendererProps) => {
  const evalState = useEvalStore((state) => ({
    evalResults: state.evalResults,
    events: state.events,
    isEvalRunning: state.isEvalRunning,
    currentEvalId: state.currentEvalId,
    currentRunId: state.currentRunId,
    currentlyTestingNodeUuid: state.currentlyTestingNodeUuid,
    isPaused: state.isPaused,
  }))
  const evalStatus = message.evalStatus
  // Only consider this message "active" (showing live state) if:
  // 1. It matches the current eval being run (both evalId AND runId)
  // 2. The eval is still running
  // 3. This message is NOT already completed/cancelled/errored (those should always show snapshot)
  const messageIsFinished =
    evalStatus === EvalMessageStatus.Completed ||
    evalStatus === EvalMessageStatus.Cancelled ||
    evalStatus === EvalMessageStatus.Error
  // Check both evalId and runId to handle "run eval with same eval id" scenario
  // where multiple messages have the same evalId but different runIds
  const runIdMatches =
    message.evalRunId === undefined ||
    evalState.currentRunId === null ||
    message.evalRunId === evalState.currentRunId
  const isActiveEval =
    Boolean(message.evalId) &&
    message.evalId === evalState.currentEvalId &&
    runIdMatches &&
    evalState.isEvalRunning &&
    !messageIsFinished

  const snapshot = message.evalSnapshot
  const displayState = isActiveEval
    ? {
        evalResults: evalState.evalResults,
        events: evalState.events,
        isEvalRunning: evalState.isEvalRunning,
        currentRunId: evalState.currentRunId,
        currentlyTestingNodeUuid: evalState.currentlyTestingNodeUuid,
        isPaused: evalState.isPaused,
      }
    : (snapshot ?? null)

  // Check if we have any nodes (created when dataset generation starts for a node)
  const hasNodes = (displayState?.evalResults.nodes?.length ?? 0) > 0

  return (
    <div className="my-2">
      <m.div
        initial={{ opacity: 0, transform: 'translateY(10px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        transition={{ duration: DURATION.mount, ease: EASE_OUT }}
        className="mr-auto w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="text-p-500">Simulation results</div>
          </div>
        </div>

        {/* Results Tree */}
        {hasNodes ? (
          <ResultsTree state={isActiveEval ? undefined : displayState} readOnly={!isActiveEval} />
        ) : (
          <div className="text-p-400 text-secondary py-4 text-center">
            {renderEvalEmptyState(evalStatus)}
          </div>
        )}
      </m.div>
    </div>
  )
}
